
import React from 'react';
import TypingIndicator from '@/components/TypingIndicator';
import ProcessingIndicator from '@/components/ProcessingIndicator';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  file?: { name: string };
  isProcessing?: boolean;
}

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
}

const MessageList = ({ messages, isLoading }: MessageListProps) => {
  const renderMessageContent = (message: Message) => {
    console.log('Rendering message:', message.text);
    console.log('Message sender:', message.sender);
    console.log('Contains HTML tags:', message.text.includes('<'));
    
    // Only render HTML for assistant messages to avoid XSS from user input
    if (message.sender === 'assistant') {
      // Check if this is a structured welcome message
      try {
        const parsedContent = JSON.parse(message.text);
        if (parsedContent.type === 'message' && parsedContent.variant === 'info') {
          return (
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 rounded-2xl p-5 shadow-lg backdrop-blur-sm">
              <div className="flex items-start space-x-3">
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                    {parsedContent.title}
                  </h3>
                  <div className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                    {parsedContent.body}
                  </div>
                </div>
              </div>
            </div>
          );
        }
      } catch {
        // If parsing fails, fall through to regular rendering
      }
      
      // Check for HTML tags (more comprehensive check)
      const hasHtmlTags = /<[^>]*>/g.test(message.text);
      
      if (hasHtmlTags) {
        console.log('Rendering as HTML for assistant message');
        return (
          <div 
            className="whitespace-pre-wrap break-words [&>*]:mb-2 [&>ul]:list-disc [&>ul]:ml-4 [&>ol]:list-decimal [&>ol]:ml-4 [&>strong]:font-bold [&>em]:italic [&>a]:text-blue-600 [&>a]:underline hover:[&>a]:text-blue-800"
            dangerouslySetInnerHTML={{ __html: message.text }}
          />
        );
      }
    }
    
    console.log('Rendering as plain text');
    // For user messages or messages without HTML, render as plain text
    return (
      <div className="whitespace-pre-wrap break-words">
        {message.text}
      </div>
    );
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {messages.map((message) => (
        <div
          key={message.id}
          className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div
            className={`${
              // Check if this is a welcome message for special styling
              (() => {
                try {
                  const parsedContent = JSON.parse(message.text);
                  if (parsedContent.type === 'message' && parsedContent.variant === 'info') {
                    return 'max-w-[95%] p-0'; // Special styling for welcome message
                  }
                } catch {}
                return `text-sm sm:text-base ${
                  message.sender === 'user'
                    ? 'chat-message-user ml-4'
                    : 'chat-message-assistant mr-4'
                }`;
              })()
            }`}
          >
            {message.isProcessing ? (
              <ProcessingIndicator />
            ) : (
              <>
                {message.file && (
                  <div className="text-xs opacity-80 mb-1">
                    📎 {message.file.name}
                  </div>
                )}
                {renderMessageContent(message)}
                <div className="text-xs opacity-60 mt-2 font-medium">
                  {message.timestamp.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      ))}
      
      {isLoading && (
        <div className="flex justify-start">
          <TypingIndicator />
        </div>
      )}
    </div>
  );
};

export default MessageList;
